import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, ShieldCheck, AlertCircle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import StripeSecurityNote from "@/components/StripeSecurityNote";

export default function PayLink({ kind = "balance" }) {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [tip, setTip] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const isDeposit = kind === "deposit";

  useEffect(() => {
    api.get(`/pay/${kind}/${token}`)
      .then((r) => setB(r.data))
      .catch((e) => setErr(apiErr(e)))
      .finally(() => setLoading(false));
  }, [token, kind]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/pay/${kind}/${token}`, { origin_url: window.location.origin, tip: isDeposit ? 0 : tip });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(apiErr(e));
      setPaying(false);
    }
  };

  const baseAmount = Number(b?.amount || 0);
  const amount = isDeposit ? baseAmount : Number((baseAmount + Number(tip || 0)).toFixed(2));
  const btnLabel = isDeposit ? `Pay 50% Deposit — $${amount.toFixed(2)}` : `Pay Remaining Balance — $${amount.toFixed(2)}`;

  return (
    <div className="min-h-screen bg-[#FBF9F5] flex flex-col items-center justify-center px-4 py-12">
      <img src="/masaya-logo.png" alt="Masaya" className="h-14 w-auto mb-8" />
      <div className="bg-white w-full max-w-md rounded-2xl border border-[#E6DEC3] p-7" data-testid="pay-link-card">
        {loading && (
          <div className="text-center py-8"><Loader2 className="animate-spin text-[#B87333] mx-auto" size={36} />
            <p className="mt-3 text-[#635B52]">Loading your {kind}…</p></div>
        )}

        {!loading && err && !b && (
          <div className="text-center py-6" data-testid="pay-link-error">
            <AlertCircle className="text-red-500 mx-auto" size={44} />
            <h1 className="mt-3 font-serif text-2xl text-[#2C2621]">Link not found</h1>
            <p className="mt-2 text-[#635B52]">{err}</p>
          </div>
        )}

        {!loading && b && b.paid && (
          <div className="text-center py-6" data-testid="pay-link-paid">
            <CheckCircle2 className="text-green-600 mx-auto" size={48} />
            <h1 className="mt-3 font-serif text-2xl text-[#2C2621]">{isDeposit ? "Deposit already paid" : "Balance already paid"}</h1>
            <p className="mt-2 text-[#635B52]">Thank you! Your {kind} for {b.booking_number} is settled.</p>
          </div>
        )}

        {!loading && b && !b.paid && (
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-[#B87333] font-semibold">{b.booking_number}</p>
            <h1 className="mt-1 font-serif text-3xl text-[#2C2621]">{isDeposit ? "Confirm your booking" : "Pay your balance"}</h1>
            <p className="mt-2 text-sm text-[#635B52]">Your {b.service_name}{b.tier_label ? ` (${b.tier_label})` : ""} on {b.date} at {b.start_time} CT{isDeposit ? "." : " is complete."}</p>

            <div className="mt-5 rounded-xl bg-[#F5F0E8] p-4 text-sm text-[#635B52] space-y-1.5" data-testid="pay-link-summary">
              <div className="flex justify-between"><span>Total</span><span className="text-[#2C2621]">${Number(b.total_price).toFixed(2)}</span></div>
              {!isDeposit && <div className="flex justify-between"><span>Deposit paid</span><span className="text-[#2C2621]">−${Number(b.deposit_amount).toFixed(2)}</span></div>}
              <div className="flex justify-between border-t border-[#E6DEC3] pt-1.5 font-semibold text-base">
                <span className="text-[#2C2621]">{isDeposit ? "Deposit due" : "Balance due"}</span>
                <span className="text-[#B87333]" data-testid="pay-link-amount">${baseAmount.toFixed(2)}</span>
              </div>
            </div>

            {!isDeposit && (
              <div className="mt-5" data-testid="tip-section">
                <p className="text-sm font-medium text-[#2C2621]">Add a tip for the crew? <span className="font-normal text-[#635B52]">(optional)</span></p>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {[0, 10, 20, 30].map((t) => (
                    <button key={t} onClick={() => { setTip(t); setCustomTip(""); }}
                      className={`rounded-full py-2 text-sm font-medium border transition-all ${Number(tip) === t && customTip === "" ? "bg-[#B87333] text-white border-[#B87333]" : "border-[#E6DEC3] text-[#2C2621] hover:bg-[#F5F0E8]"}`}
                      data-testid={`tip-${t}`}>{t === 0 ? "No tip" : `$${t}`}</button>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-[#635B52]">Custom $</span>
                  <input type="number" min="0" step="1" value={customTip}
                    onChange={(e) => { setCustomTip(e.target.value); setTip(Math.max(0, Number(e.target.value) || 0)); }}
                    placeholder="0" className="w-28 rounded-xl border border-[#E6DEC3] px-3 py-2 text-sm" data-testid="tip-custom" />
                  {tip > 0 && <span className="text-sm text-[#635B52]">Tip: <span className="text-[#2C2621] font-medium" data-testid="tip-amount">${Number(tip).toFixed(2)}</span></span>}
                </div>
                {tip > 0 && (
                  <div className="mt-3 flex justify-between text-sm font-semibold border-t border-[#E6DEC3] pt-2">
                    <span className="text-[#2C2621]">Total charged</span>
                    <span className="text-[#2C2621]" data-testid="pay-total-with-tip">${amount.toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}

            {err && <p className="mt-3 text-sm text-red-600" data-testid="pay-link-inline-error">{err}</p>}

            <button onClick={pay} disabled={paying}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#B87333] text-white rounded-full px-6 py-3 font-medium disabled:opacity-60"
              data-testid="pay-link-btn">
              {paying ? <><Loader2 className="animate-spin" size={18} /> Redirecting…</> : btnLabel}
            </button>
            <StripeSecurityNote className="mt-3" />
          </div>
        )}
      </div>
    </div>
  );
}
